import { AxiosResponse } from 'axios';
import mapKeys from 'lodash-es/mapKeys';
import { StateCreator } from 'zustand';

import { SECURE_API } from 'src/api/api';
import { IEdition, normalizeEditions } from 'src/interfaces/Edition';
import { IFormEdition } from 'src/interfaces/Edition';

export interface EditionSlice {
  activeEditionId?: string;
  editions: IEdition[];
  editionsById?: { [key: string]: IEdition };
  editionsByYear?: { [key: string]: IEdition };

  isLoading: boolean;

  deleteAllEditions: () => Promise<void | AxiosResponse<null>>;
  deleteEdition: (id: string) => Promise<void | AxiosResponse<null>>;
  fetchActiveEdition: () => Promise<void | AxiosResponse<null>>;
  fetchEditions: () => Promise<void | AxiosResponse<null>>;
  resetEditions: () => void;
  saveActiveEdition: (
    activeEdition: IFormEdition,
    id?: string,
  ) => Promise<void | AxiosResponse<null>>;
  saveEdition: (edition: IFormEdition, id?: string) => Promise<void | AxiosResponse<IEdition[]>>;
  saveStatisticsForEdition: (id: string) => Promise<void | AxiosResponse<{
    statistics: unknown;
    checkinsByPrice: Record<string, number>;
    totalPrice: number;
  }>>;
  toggleActiveEdition: (editionId: string) => Promise<void>;
}

export const createEditionsSlice: StateCreator<EditionSlice, [], [], EditionSlice> = (set, get) => {
  const getEditions = async () => {
    return SECURE_API.get<IEdition[]>('editions/')
      .then(({ data }) => {
        const editions = normalizeEditions(data);
        const editionsById = mapKeys(editions, 'id');

        set({
          editions,
          editionsById,
          editionsByYear: mapKeys(editions, 'year'),
          isLoading: false,
        });
      })
      .catch((error: string) => {
        set({
          editions: [],
          editionsById: undefined,
          editionsByYear: undefined,
          isLoading: false,
        });
        console.log({ error });
      });
  };

  return {
    editions: [],

    isLoading: false,

    deleteAllEditions: async () => {
      set({ isLoading: true });
      return SECURE_API.delete<null>(`editions/delete/bulk`)
        .catch((error: string) => {
          console.log({ error });
        })
        .finally(() => {
          getEditions();
        });
    },
    deleteEdition: async id => {
      set({ isLoading: true });
      return SECURE_API.delete<null>(`editions/delete/${id}`)
        .catch((error: string) => {
          console.log({ error });
        })
        .finally(() => {
          getEditions();
        });
    },
    fetchActiveEdition: async () => {
      set({ isLoading: true });
      return SECURE_API.get<{
        id: string;
        activeEditionId?: string;
      }>('activeEdition/')
        .then(({ data }) => {
          set({
            activeEditionId: data.activeEditionId,
            isLoading: false,
          });
        })
        .catch((error: string) => {
          set({
            activeEditionId: undefined,
            isLoading: false,
          });
          console.log({ error });
        });
    },

    fetchEditions: async () => {
      set({ isLoading: true });
      return getEditions();
    },

    resetEditions: () => set({ editions: [] }),
    saveEdition: async (edition: IFormEdition, id?: string) => {
      set({ isLoading: true });
      const saveEditionPromise = id
        ? SECURE_API.put<IEdition[]>(`editions/update/${id}`, edition)
        : SECURE_API.post<IEdition[]>(`editions/save`, edition);
      return saveEditionPromise
        .catch((error: string) => {
          console.log({ error });
        })
        .finally(() => {
          getEditions();
        });
    },
    saveActiveEdition: async (activeEdition: IFormEdition, id?: string) => {
      set({ isLoading: true });

      return SECURE_API.put<null>(`editions/${id}`, activeEdition)
        .catch((error: string) => {
          console.log({ error });
        })
        .finally(() => {
          getEditions();
        });
    },
    saveStatisticsForEdition: async (id: string) => {
      set({ isLoading: true });
      return SECURE_API.put<{
        statistics: unknown;
        checkinsByPrice: Record<string, number>;
        totalPrice: number;
      }>(`editions/recompute-statistics/${id}`)
        .catch((error: string) => {
          console.log({ error });
        })
        .finally(() => {
          getEditions();
        });
    },

    toggleActiveEdition: async (id: string) => {
      set({ isLoading: true });
      const editionId = id === get().activeEditionId ? undefined : id;
      return SECURE_API.put<{
        id: string;
        activeEditionId?: string;
      }>('activeEdition/update/', { editionId })
        .then(({ data }) => {
          set({
            activeEditionId: data.activeEditionId,
            isLoading: false,
          });
        })
        .catch((error: string) => {
          set({
            activeEditionId: undefined,
            isLoading: false,
          });
          console.log({ error });
        });
    },
  };
};
